import { useEffect, useRef, useState } from "react";
import {
  audiogramFromTrials,
  tonePlan,
  type Ear,
  type ToneTrial,
} from "@hearing/shared";
import { Layout } from "../components/Layout.js";
import { Button } from "../components/Button.js";
import { TonePlayer } from "../components/TonePlayer.js";
import { useScreener } from "../state.js";
import { useAudio } from "../audio/AudioProvider.js";
import { useFlow } from "./useFlow.js";

type Phase = "ear-intro" | "adjusting" | "saving";

/**
 * The measurement: for each ear and frequency a continuous pulsing tone plays,
 * starting clearly audible. The user turns it down with − until they can only
 * just hear it, then confirms. That level is the threshold for the point.
 */
export function Measurement() {
  const { config, setToneResult } = useScreener();
  const { ensureEngine, getEngine } = useAudio();
  const { go } = useFlow();

  const plan = config ? tonePlan(config.tone) : [];
  const [planIndex, setPlanIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>("ear-intro");
  const [attenuationDb, setAttenuationDb] = useState(
    config?.tone.startAttenuationDb ?? 10,
  );
  const [error, setError] = useState<string | null>(null);
  const trials = useRef<ToneTrial[]>([]);

  const item = plan[planIndex];

  // Stop any tone when the screen unmounts.
  useEffect(() => () => getEngine()?.stopTone(), [getEngine]);

  if (!config || !item) return null;
  const { tone } = config;
  const step = tone.adjustStepDb;

  async function startCurrentTone(atten: number) {
    if (!config) return;
    try {
      const engine = await ensureEngine(config.audio, config.din);
      engine.startTone(item.frequency, atten, item.ear);
      setError(null);
    } catch {
      setError("We couldn't play audio. Check your device and try again.");
    }
  }

  async function begin() {
    setAttenuationDb(tone.startAttenuationDb);
    setPhase("adjusting");
    await startCurrentTone(tone.startAttenuationDb);
  }

  function adjust(deltaDb: number) {
    setAttenuationDb((prev) => {
      const next = Math.max(0, Math.min(tone.maxAttenuationDb, prev + deltaDb));
      getEngine()?.setToneAttenuation(next);
      return next;
    });
  }

  async function advance(trial: ToneTrial) {
    trials.current.push(trial);
    getEngine()?.stopTone();

    const nextIndex = planIndex + 1;
    if (nextIndex >= plan.length) {
      setPhase("saving");
      const audiogram = audiogramFromTrials(trials.current, tone);
      // Stash results client-side; everything is submitted once at the end.
      setToneResult({ audiogram, toneTrials: trials.current });
      go("questions");
      return;
    }

    const cfg = config!;
    const earChanges = plan[nextIndex].ear !== item.ear;
    setPlanIndex(nextIndex);
    setAttenuationDb(tone.startAttenuationDb);
    if (earChanges) {
      setPhase("ear-intro");
    } else {
      // Brief gap, then the next frequency starts automatically.
      const next = plan[nextIndex];
      setTimeout(async () => {
        try {
          const engine = await ensureEngine(cfg.audio, cfg.din);
          engine.startTone(next.frequency, tone.startAttenuationDb, next.ear);
        } catch {
          setError("We couldn't play audio. Check your device and try again.");
        }
      }, 400);
    }
  }

  function confirmThreshold() {
    void advance({
      frequency: item.frequency,
      ear: item.ear,
      attenuationDb,
      heard: true,
    });
  }

  function cannotHear() {
    void advance({
      frequency: item.frequency,
      ear: item.ear,
      attenuationDb: 0,
      heard: false,
    });
  }

  const earLabel = item.ear === "left" ? "left" : "right";
  const progress = planIndex / plan.length;

  if (phase === "saving") {
    return (
      <Layout step="test">
        <div className="saving-state" role="status">
          <span className="saving-spinner" aria-hidden />
          <h2>Test complete</h2>
          <p className="muted">Preparing the final questions…</p>
        </div>
      </Layout>
    );
  }

  if (phase === "ear-intro") {
    return (
      <Layout step="test">
        <div className="screen-illustration" aria-hidden>
          <EarBadge ear={item.ear} />
        </div>
        <h2 className="center">
          {planIndex === 0 ? "Let's test your " : "Now your "}
          <strong>{earLabel} ear</strong>
        </h2>
        <p className="muted center screen-copy">
          A pulsing tone will play in your {earLabel} ear, starting clearly
          audible. Tap <strong>Quieter</strong> to turn it down until you can{" "}
          <strong>only just hear it</strong>, then confirm. This finds the{" "}
          <strong>softest sound you can detect</strong>.
        </p>
        <div className="actions center-actions">
          <Button onClick={begin}>Start</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout step="test">
      <div
        className="progress-line"
        role="progressbar"
        aria-label={`Tone ${planIndex + 1} of ${plan.length}`}
        aria-valuemin={0}
        aria-valuemax={plan.length}
        aria-valuenow={planIndex}
      >
        <span style={{ width: `${Math.round(progress * 100)}%` }} />
      </div>

      <div className="test-status">
        <span className="status-chip">{earLabel} ear</span>
        <span className="status-chip">{formatFrequency(item.frequency)}</span>
        <span className="status-chip">
          {planIndex + 1} of {plan.length}
        </span>
      </div>
      <h2 className="center">
        Make the tone as quiet as you can while still hearing it
      </h2>
      <p className="tone-hint">
        Tap <strong>Quieter</strong> until you can only just hear the tone, then
        confirm. If it gets too quiet, tap <strong>Louder</strong>.
      </p>

      <div className="test-workspace">
        <TonePlayer
          playing
          onQuieter={() => adjust(step)}
          onLouder={() => adjust(-step)}
          canQuieter={attenuationDb < tone.maxAttenuationDb}
          canLouder={attenuationDb > 0}
        />
      </div>

      {error ? <div className="note">{error}</div> : null}

      <div className="actions center-actions">
        <Button onClick={confirmThreshold}>
          This is the quietest I can still hear
        </Button>
      </div>
      <p className="center">
        <button className="link-button" onClick={cannotHear}>
          I can't hear this tone at all
        </button>
      </p>
    </Layout>
  );
}

function EarBadge({ ear }: { ear: Ear }) {
  return (
    <div className={"ear-badge " + ear}>
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none">
        <path
          d="M6 8.5a6 6 0 0111.9 1.2c0 2.4-1.4 3.4-2.6 4.6-1 1-1.6 1.8-1.8 3.2-.2 1.5-1.3 2.5-2.8 2.5-1 0-1.9-.5-2.4-1.4"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M9.5 9.5a2.8 2.8 0 015.4.9c0 1.1-.7 1.7-1.5 2.4"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
      <span className="ear-badge-label">{ear === "left" ? "L" : "R"}</span>
    </div>
  );
}

function formatFrequency(frequency: number): string {
  return frequency >= 1000 ? `${frequency / 1000} kHz` : `${frequency} Hz`;
}
